/**
 * A message template box with the allowed variables as chips (TRUS-686).
 * Clicking a chip inserts `{{name}}` at the cursor. Variables outside the
 * allow-list are flagged under the box, since the server would leave them
 * blank. No member details beyond the allow-list exist to insert.
 */

import React, { useRef } from 'react';
import { AlertTriangle, Braces } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Textarea } from '@/components/ui/textarea';
import { TEMPLATE_VARIABLES, unknownTemplateVariables, type TemplateVariable } from './notifySteps';

const VARIABLE_HINT: Record<TemplateVariable, [string, string]> = {
  program_name: ['workflows.notify.vars.programName', 'The program'],
  step_name: ['workflows.notify.vars.stepName', 'This step'],
  workflow_name: ['workflows.notify.vars.workflowName', 'This workflow'],
  due_date: ['workflows.notify.vars.dueDate', 'The due date'],
  assignee_name: ['workflows.notify.vars.assigneeName', 'Who it is assigned to'],
  member_first_initial: ['workflows.notify.vars.memberInitial', 'The member’s first initial, e.g. "M."'],
  link: ['workflows.notify.vars.link', 'A link to the item'],
};

interface MessageTemplateEditorProps {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
  /** Single line (e.g. an email subject or a task title). */
  singleLine?: boolean;
}

export function MessageTemplateEditor({
  id,
  label,
  value,
  onChange,
  placeholder,
  disabled,
  rows = 3,
  singleLine,
}: MessageTemplateEditorProps) {
  const { t } = useAppTranslation();
  const ref = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);
  const unknown = unknownTemplateVariables(value);

  const insert = (name: string) => {
    const token = `{{${name}}}`;
    const el = ref.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className="space-y-1.5" data-testid={`template-editor-${id}`}>
      <label htmlFor={id} className="block text-sm font-medium">
        {label}
      </label>
      {singleLine ? (
        <input
          id={id}
          ref={ref as React.RefObject<HTMLInputElement>}
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={unknown.length > 0 || undefined}
        />
      ) : (
        <Textarea
          id={id}
          ref={ref as React.RefObject<HTMLTextAreaElement>}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
          aria-invalid={unknown.length > 0 || undefined}
        />
      )}
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={t('workflows.notify.insertVariable', 'Insert a variable')}>
        <Braces className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        {TEMPLATE_VARIABLES.map((name) => (
          <button
            key={name}
            type="button"
            className="rounded-full border border-border bg-muted px-2 py-0.5 font-mono text-[11px] text-foreground hover:bg-accent disabled:opacity-50"
            onClick={() => insert(name)}
            disabled={disabled}
            title={t(VARIABLE_HINT[name][0], VARIABLE_HINT[name][1])}
            data-testid={`template-chip-${name}`}
          >
            {`{{${name}}}`}
          </button>
        ))}
      </div>
      {unknown.length > 0 && (
        <p className="flex items-start gap-1 text-xs text-destructive" role="alert" data-testid={`template-unknown-${id}`}>
          <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" aria-hidden="true" />
          {t(
            'workflows.notify.issues.unknownVariables',
            'These variables are not allowed and would be left blank: {{list}}',
            { list: unknown.map((v) => `{{${v}}}`).join(', ') },
          )}
        </p>
      )}
    </div>
  );
}

export default MessageTemplateEditor;
